import { computed, ref } from 'vue';
import axios from 'axios';
import route from 'ziggy';
import {
	cookiePreference,
	isThemePreference,
	resolveTheme,
	rootState,
	startingPreference,
	themeCookie,
	THEME_STORAGE_KEY,
	type ResolvedTheme,
	type ThemePreference,
} from './themeLogic';

// The look of the app (docs/UI_THEME.md): System, Light or Dark. The first paint is decided by the inline script of the root view; this keeps the choice in step afterwards: it
// applies a new choice at once, mirrors it in the browser (storage and a cookie), stores it in the person's settings when there is a person, and follows the system while the
// choice is "system".
const preference = ref<ThemePreference>('system');
const systemDark = ref(false);
let started = false;
let query: MediaQueryList | null = null;

const safely = <T>(read: () => T, otherwise: T): T => {
	try {
		return read();
	} catch {
		return otherwise;
	}
};

const apply = () => {
	const resolved = resolveTheme(preference.value, systemDark.value);
	const state = rootState(preference.value, resolved);
	const root = document.documentElement;

	root.classList.toggle('dark', state.dark);
	root.setAttribute('data-theme', state.theme);
	root.setAttribute('data-theme-pref', state.pref);
	root.style.colorScheme = state.colorScheme;
};

const remember = () => {
	safely(
		() => window.localStorage.setItem(THEME_STORAGE_KEY, preference.value),
		undefined,
	);
	safely(() => {
		document.cookie = themeCookie(preference.value);
	}, undefined);
};

/**
 * Starts the theme once per page load: reads the choice (the person's, else the browser's, else the default), applies it and follows the system. `shared` is the Inertia prop
 * `theme` ({ preference, default }).
 */
export const initTheme = (shared?: {
	preference?: unknown;
	default?: unknown;
	scope?: unknown;
}) => {
	if (typeof window === 'undefined' || started) return;
	started = true;

	// not signed in: the root view painted the look those pages always had and nothing here changes it
	if (shared?.scope === 'public') return;

	query = safely(() => window.matchMedia('(prefers-color-scheme: dark)'), null);
	systemDark.value = query?.matches ?? false;
	preference.value = startingPreference({
		server: shared?.preference,
		stored: safely(() => window.localStorage.getItem(THEME_STORAGE_KEY), null),
		cookie: cookiePreference(safely(() => document.cookie, '')),
		fallback: shared?.default,
	});
	apply();
	remember();

	query?.addEventListener?.('change', (event) => {
		systemDark.value = event.matches;
		if (preference.value === 'system') apply();
	});
};

export const useTheme = () => {
	const resolved = computed<ResolvedTheme>(() =>
		resolveTheme(preference.value, systemDark.value),
	);

	const set = async (next: ThemePreference): Promise<void> => {
		if (!isThemePreference(next) || next === preference.value) return;

		preference.value = next;
		apply();
		remember();

		// stored for the person, when there is one on this domain (the admin area has no such route: the browser's copy is all it keeps)
		const stored = safely(
			() => route().has('settings.theme.update') as boolean,
			false,
		);

		if (!stored) return;

		try {
			await axios.patch(route('settings.theme.update'), { theme: next });
		} catch {
			// the browser keeps the choice; the next load tries the server again only when the person chooses again
		}
	};

	return { preference, resolved, set };
};
